import { useEffect, useId, useRef, useState } from 'react'
import Icon from '../docs/Icon'
import { shareUrl, usePrefs } from '@/state/prefs'
import { useBrand } from '@/state/useBrand'
import { useCasing } from '@/state/useCasing'
import { designKitFiles, kitName } from './designKit'
import { zip } from './zip'
import { fontFiles } from './fontFiles'

const HINT_KEY = 'mds:design-hint'

async function text(path: string) {
  const response = await fetch(path)
  if (!response.ok) throw new Error(`${path}: ${response.status}`)
  return response.text()
}

/** The published stylesheets and the chosen typefaces' font files. */
async function kitSources(fontIds: string[]) {
  const [tokens, components, ...files] = await Promise.all([
    text('/mds/tokens.css'),
    text('/mds/components.css'),
    ...fontIds.map(async (id) => {
      const url = (await fontFiles[id]()).default
      const response = await fetch(url)
      if (!response.ok) throw new Error(`${id}: ${response.status}`)
      return [id, new Uint8Array(await response.arrayBuffer())] as const
    }),
  ])
  return { tokens, components, fonts: Object.fromEntries(files) }
}

/**
 * The floating design bar: a red Edit Design pill that expands to the left
 * into the tools: the design mode switch, reset (after a quick confirm),
 * download the current design as a kit, and share a link to it.
 */
export default function DesignNotesBar() {
  const { prefs, set, reset } = usePrefs()
  const brand = useBrand()
  const cased = useCasing()
  const [confirming, setConfirming] = useState(false)
  const [status, setStatus] = useState('')
  const [expanded, setExpanded] = useState(false)
  const [shared, setShared] = useState(false)
  const toolsId = useId()

  // A hint for visitors who have not opened it: the bar sneaks out twice on its
  // own, a little after a moment, then further ten seconds later, and closes
  // again each time. Once a visitor opens it, it is remembered and never
  // hints again.
  const [peek, setPeek] = useState<0 | 1 | 2>(0)
  const [engaged, setEngaged] = useState(() => {
    try {
      return window.localStorage.getItem(HINT_KEY) === 'opened'
    } catch {
      return false
    }
  })
  useEffect(() => {
    if (engaged || expanded) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timers = [
      window.setTimeout(() => setPeek(1), 2500),
      window.setTimeout(() => setPeek(0), 4300),
      window.setTimeout(() => setPeek(2), 12500),
      window.setTimeout(() => setPeek(0), 14800),
    ]
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer))
      setPeek(0)
    }
  }, [engaged, expanded])
  const engage = () => {
    setEngaged(true)
    try {
      window.localStorage.setItem(HINT_KEY, 'opened')
    } catch {
      // Without storage it stops hinting for this visit.
    }
  }

  // Share: the address becomes the design's own link, which a phone hands to
  // its share sheet and a desktop copies.
  const share = async () => {
    const url = shareUrl(prefs)
    window.history.replaceState(window.history.state, '', url)
    const phone = window.matchMedia('(pointer: coarse)').matches && typeof navigator.share === 'function'
    if (phone) {
      try {
        await navigator.share({ title: brand.name, text: 'A design from Mission Design System', url })
        setStatus('Design shared.')
      } catch {
        // Closing the share sheet leaves the link in the address bar.
      }
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      setStatus('Link to this design copied.')
      setShared(true)
      window.setTimeout(() => setShared(false), 2000)
    } catch {
      setStatus('The link to this design is in the address bar.')
    }
  }
  // The confirmation takes focus on Cancel, so a stray Enter keeps the choices.
  const cancel = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (confirming) cancel.current?.focus()
  }, [confirming])

  const download = async () => {
    setStatus('Preparing the design kit…')
    try {
      const sources = await kitSources([prefs.fontDisplay, prefs.fontSans, 'geist-mono'].filter((id) => id in fontFiles))
      const name = kitName(prefs, brand)
      const files = designKitFiles(prefs, brand.mission, brand, sources, name)
      const url = URL.createObjectURL(new Blob([zip(files)], { type: 'application/zip' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `${name}.zip`
      link.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
      setStatus('Design kit downloaded.')
    } catch {
      setStatus('The design kit could not be prepared. Try again.')
    }
  }

  return (
    <div
      className={`mds-notes-bar${expanded ? ' is-expanded' : ''}${peek && !expanded ? ` is-peeking is-peeking-${peek}` : ''}`}
      role="region"
      aria-label={cased('Design tools')}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        if (confirming) setConfirming(false)
        else if (expanded) setExpanded(false)
      }}
    >
      {/* First in tab order, last visually: the row lays out right to left so
          the tools open toward the left of the pill. */}
      <button
        type="button"
        className="mds-notes-bar__pill"
        aria-expanded={expanded}
        aria-controls={toolsId}
        onClick={() => {
          engage()
          setExpanded((open) => !open)
        }}
      >
        <Icon name="pen" size={15} />
        {cased('Edit design')}
      </button>
      <span id={toolsId} className="mds-notes-bar__tools">
        <button
          type="button"
          role="switch"
          aria-checked={prefs.notes}
          className="mds-notes-bar__button mds-notes-bar__edit"
          onClick={() => set('notes', !prefs.notes)}
        >
          <span className="mds-switch-track" aria-hidden="true" />
          {cased('Design mode')}
        </button>
        <button type="button" className="mds-notes-bar__button" onClick={() => setConfirming(true)}>
          <Icon name="reset" size={15} />
          {cased('Reset')}
        </button>
        <button type="button" className="mds-notes-bar__button" onClick={download}>
          <Icon name="download" size={15} />
          {cased('Download')}
        </button>
        <button type="button" className="mds-notes-bar__button" onClick={() => void share()}>
          <Icon name={shared ? 'check' : 'share'} size={15} />
          {cased(shared ? 'Link copied' : 'Share')}
        </button>
      </span>
      <p className="mds-visually-hidden" role="status">
        {status}
      </p>
      {confirming && (
        <div className="mds-notes-bar__confirm" role="group" aria-label={cased('Reset the design')}>
          <span>{cased('Reset every choice?')}</span>
          <button
            type="button"
            className="mds-button mds-button--primary mds-button--sm"
            onClick={() => {
              reset()
              setConfirming(false)
              setStatus('The design is back to the default.')
            }}
          >
            {cased('Reset')}
          </button>
          <button ref={cancel} type="button" className="mds-button mds-button--ghost mds-button--sm" onClick={() => setConfirming(false)}>
            {cased('Cancel')}
          </button>
        </div>
      )}
    </div>
  )
}
